import React, { useState } from 'react';
import './Bookings.css';

const SESSION_STATUS_META = {
  scheduled: { label: 'Upcoming', color: '#3498db', bg: '#eaf4fb' },
  pending: { label: 'Upcoming', color: '#3498db', bg: '#eaf4fb' },
  completed: { label: 'Completed', color: '#27ae60', bg: '#e9f7ef' },
  'no-show': { label: 'No-show', color: '#e67e22', bg: '#fdf1e6' },
  canceled: { label: 'Canceled', color: '#95a5a6', bg: '#f2f3f4' }
};

// datetime-local input value <-> Date
function toLocalInputValue(d) {
  if (!d) return '';
  const dt = new Date(d);
  const pad = n => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function SessionRow({ session, planId, canMark, canSchedule, onMarkSession, onScheduleSession, busy }) {
  const [picking, setPicking] = useState(false);
  const [when, setWhen] = useState(toLocalInputValue(session.scheduledDate));

  const meta = SESSION_STATUS_META[session.status] || SESSION_STATUS_META.scheduled;
  const isResolved = ['completed', 'no-show', 'canceled'].includes(session.status);
  const dateStr = session.scheduledDate
    ? new Date(session.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '⏳ not scheduled';

  const btn = (label, status, color) => (
    <button
      onClick={() => onMarkSession(planId, session.sequenceNumber, status)}
      disabled={busy}
      style={{
        padding: '4px 8px', fontSize: '11px', fontWeight: 'bold', borderRadius: '4px',
        border: `1px solid ${color}`, background: '#fff', color,
        cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1
      }}
    >
      {label}
    </button>
  );

  const submitSchedule = async () => {
    if (!when) return;
    await onScheduleSession(planId, session.sequenceNumber, new Date(when).toISOString());
    setPicking(false);
  };

  return (
    <div style={{
      padding: '8px 0',
      borderBottom: '1px solid #eef1f4',
      fontSize: '13px',
      color: '#2c3e50'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'bold', minWidth: '28px', color: '#7f8c8d' }}>#{session.sequenceNumber}</span>
        <span style={{ minWidth: '140px', color: session.scheduledDate ? '#2c3e50' : '#e67e22', fontWeight: session.scheduledDate ? 'normal' : 'bold' }}>{dateStr}</span>
        <span style={{
          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold',
          color: meta.color, background: meta.bg
        }}>
          {meta.label}{session.markedBy === 'auto' ? ' · auto' : ''}
        </span>

        <span style={{ display: 'flex', gap: '6px', marginLeft: 'auto', flexWrap: 'wrap' }}>
          {canSchedule && !isResolved && (
            <button
              onClick={() => setPicking(p => !p)}
              disabled={busy}
              style={{
                padding: '4px 8px', fontSize: '11px', fontWeight: 'bold', borderRadius: '4px',
                border: '1px solid #3498db', background: '#fff', color: '#3498db',
                cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1
              }}
            >
              {session.scheduledDate ? '✏️ Reschedule' : '📅 Schedule'}
            </button>
          )}
          {canMark && !isResolved && (
            <>
              {btn('✅ Done', 'completed', '#27ae60')}
              {btn('🚫 No-show', 'no-show', '#e67e22')}
              {btn('❌ Cancel', 'canceled', '#95a5a6')}
            </>
          )}
          {canMark && isResolved && btn('↩ Undo', 'scheduled', '#3498db')}
        </span>
      </div>

      {picking && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
          <input
            type="datetime-local"
            value={when}
            onChange={e => setWhen(e.target.value)}
            style={{ padding: '6px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
          />
          <button
            onClick={submitSchedule}
            disabled={busy || !when}
            style={{
              padding: '6px 10px', fontSize: '12px', fontWeight: 'bold', borderRadius: '4px',
              border: 'none', background: '#3498db', color: '#fff',
              cursor: (busy || !when) ? 'not-allowed' : 'pointer', opacity: (busy || !when) ? 0.5 : 1
            }}
          >
            Save time
          </button>
          <button
            onClick={() => setPicking(false)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ddd', background: 'white', color: '#7f8c8d', cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export default function CoachingPlanCard({
  plan,
  onBook,
  onDelete,
  onMarkSession,
  onScheduleSession,
  onAssign,
  students = [],
  showActions = false,
  isCoach = false
}) {
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignPlayerId, setAssignPlayerId] = useState('');

  const progress = plan.progress || null;
  const canMark = isCoach && ['booked', 'in-progress', 'completed'].includes(plan.bookingStatus);
  const canSchedule = !!onScheduleSession && ['booked', 'in-progress'].includes(plan.bookingStatus);
  const lowSessions = progress && progress.remaining > 0 && progress.remaining <= 2;
  const unscheduled = progress ? (progress.unscheduled || 0) : 0;

  const handleMark = async (planId, sequenceNumber, status) => {
    if (!onMarkSession) return;
    setBusy(true);
    try {
      await onMarkSession(planId, sequenceNumber, status);
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = async () => {
    if (!onAssign || !assignPlayerId) return;
    setBusy(true);
    try {
      await onAssign(plan._id, assignPlayerId);
      setAssigning(false);
      setAssignPlayerId('');
    } finally {
      setBusy(false);
    }
  };

  const handleSchedule = async (planId, sequenceNumber, startTimeISO) => {
    if (!onScheduleSession) return;
    setBusy(true);
    try {
      await onScheduleSession(planId, sequenceNumber, startTimeISO);
    } finally {
      setBusy(false);
    }
  };

  const getStatusBadge = () => {
    if (plan.bookingStatus === 'booked') return '📖 Booked';
    if (plan.bookingStatus === 'in-progress') return '🔄 In Progress';
    if (plan.bookingStatus === 'completed') return '✅ Completed';
    if (plan.bookingStatus === 'archived') return '📦 Archived';
    return '🟢 Available';
  };

  const getStatusColor = () => {
    if (plan.bookingStatus === 'booked') return '#3498db';
    if (plan.bookingStatus === 'in-progress') return '#f39c12';
    if (plan.bookingStatus === 'completed') return '#27ae60';
    if (plan.bookingStatus === 'archived') return '#7f8c8d';
    return '#27ae60';
  };

  const getSessionTypeEmoji = () => {
    const emojiMap = {
      'technique': '🎾',
      'match-play': '🏆',
      'drill-practice': '🎯',
      'match-prep': '📋',
      'mental': '🧠',
      'custom': '📝'
    };
    return emojiMap[plan.sessionType] || '📝';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCoachInfo = () => {
    if (typeof plan.coachId === 'string') return null;
    return plan.coachId;
  };

  const coach = getCoachInfo();

  return (
    <div style={{
      padding: '16px',
      border: `2px solid ${getStatusColor()}`,
      borderRadius: '8px',
      marginBottom: '12px',
      background: plan.bookingStatus === 'archived' ? '#f8f9fa' : 'white',
      opacity: plan.bookingStatus === 'archived' ? 0.7 : 1
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <div>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#2c3e50' }}>
            {getSessionTypeEmoji()} {plan.name}
          </h4>
          {!isCoach && coach && (
            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '4px' }}>
              👨‍🏫 {coach.name}
            </div>
          )}
          {isCoach && plan.bookedByPlayerId && typeof plan.bookedByPlayerId === 'object' && (
            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '4px' }}>
              👤 {plan.bookedByPlayerId.name}
            </div>
          )}
          <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
            📅 {formatDate(plan.startDate)} to {formatDate(plan.endDate)}
          </div>
        </div>

        <span style={{
          padding: '4px 8px',
          background: getStatusColor(),
          color: 'white',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          whiteSpace: 'nowrap'
        }}>
          {getStatusBadge()}
        </span>
      </div>

      {/* Progress bar - shown once a plan is booked and progress is available */}
      {progress && ['booked', 'in-progress', 'completed'].includes(plan.bookingStatus) && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#2c3e50',
            marginBottom: '4px'
          }}>
            <span>{progress.used} / {progress.total} sessions used</span>
            <span style={{ color: lowSessions ? '#e67e22' : '#7f8c8d' }}>
              {progress.remaining} left{progress.canceled > 0 ? ` · ${progress.canceled} canceled` : ''}
            </span>
          </div>
          <div style={{ height: '8px', background: '#ecf0f1', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${progress.total ? Math.round((progress.used / progress.total) * 100) : 0}%`,
              height: '100%',
              background: progress.isComplete ? '#27ae60' : '#3498db',
              transition: 'width 0.3s ease'
            }} />
          </div>
          {progress.nextSession && !progress.isComplete && (
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
              ⏭️ Next: session #{progress.nextSession.sequenceNumber} on {formatDate(progress.nextSession.scheduledDate)}
            </div>
          )}
          {unscheduled > 0 && !progress.isComplete && (
            <div style={{ fontSize: '12px', color: '#e67e22', marginTop: '4px' }}>
              📅 {unscheduled} session{unscheduled !== 1 ? 's' : ''} still need a date/time
            </div>
          )}
          {lowSessions && !progress.isComplete && (
            <div style={{ fontSize: '12px', color: '#e67e22', fontWeight: 'bold', marginTop: '4px' }}>
              ⚠️ Only {progress.remaining} session{progress.remaining !== 1 ? 's' : ''} left in this pack
            </div>
          )}
        </div>
      )}

      {plan.description && (
        <div style={{
          fontSize: '13px',
          color: '#2c3e50',
          marginBottom: '12px',
          paddingBottom: '12px',
          borderBottom: '1px solid #e0e6ed',
          fontStyle: 'italic'
        }}>
          "{plan.description}"
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
        fontSize: '13px',
        color: '#2c3e50',
        marginBottom: '12px',
        paddingBottom: '12px',
        borderBottom: '1px solid #e0e6ed'
      }}>
        <div>
          <strong>📊 Sessions:</strong> {plan.sessionCount}
        </div>
        <div>
          <strong>⏱️ Duration:</strong> {plan.sessionDurationMinutes} min each
        </div>
        <div>
          <strong>🔄 Frequency:</strong> {plan.sessionFrequency === 'biweekly' ? 'Bi-weekly' : 'Weekly'}
        </div>
        <div>
          <strong>👥 Max:</strong> {plan.maxBookings} person{plan.maxBookings !== 1 ? 's' : ''}
        </div>
      </div>

      {(plan.totalPrice || plan.pricePerSession) && (
        <div style={{
          fontSize: '13px',
          color: '#e74c3c',
          fontWeight: 'bold',
          marginBottom: '12px',
          paddingBottom: '12px',
          borderBottom: '1px solid #e0e6ed'
        }}>
          💰 {plan.totalPrice && `$${plan.totalPrice.toFixed(2)} total`}
          {plan.totalPrice && plan.pricePerSession && ' • '}
          {plan.pricePerSession && `$${plan.pricePerSession.toFixed(2)}/session`}
        </div>
      )}

      {plan.tags && plan.tags.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {plan.tags.map((tag, idx) => (
            <span
              key={idx}
              style={{
                fontSize: '11px',
                padding: '3px 8px',
                background: '#ecf0f1',
                borderRadius: '3px',
                color: '#34495e'
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Session checklist - the coach's per-session mark controls, or a
          read-only session list for the player once a plan is booked */}
      {Array.isArray(plan.bookingSlots) && plan.bookingSlots.length > 0 &&
        ['booked', 'in-progress', 'completed'].includes(plan.bookingStatus) && (
        <div style={{ marginBottom: showActions ? '12px' : 0 }}>
          <button
            onClick={() => setSessionsOpen(o => !o)}
            style={{
              width: '100%',
              padding: '8px 10px',
              background: '#f8f9fa',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold',
              color: '#2c3e50',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <span>📋 Sessions ({plan.bookingSlots.length})</span>
            <span>{sessionsOpen ? '▼' : '▶'}</span>
          </button>
          {sessionsOpen && (
            <div style={{ padding: '4px 10px 0' }}>
              {[...plan.bookingSlots]
                .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0))
                .map(session => (
                  <SessionRow
                    key={session.sequenceNumber}
                    session={session}
                    planId={plan._id}
                    canMark={canMark}
                    canSchedule={canSchedule}
                    onMarkSession={handleMark}
                    onScheduleSession={handleSchedule}
                    busy={busy}
                  />
                ))}
            </div>
          )}
        </div>
      )}

      {/* Assign to a student - the only way an "available" plan the coach
          created ever gets used, short of a player independently finding
          and self-booking it. Without this, a coach-only "available" plan
          has no action besides Delete. */}
      {isCoach && plan.bookingStatus === 'available' && onAssign && (
        <div style={{ marginBottom: '12px' }}>
          {!assigning ? (
            <button
              onClick={() => setAssigning(true)}
              style={{
                width: '100%',
                padding: '8px 10px',
                background: '#eaf4fb',
                border: '1px solid #3498db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                color: '#3498db'
              }}
            >
              👤 Assign to Student
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={assignPlayerId}
                onChange={e => setAssignPlayerId(e.target.value)}
                disabled={busy}
                style={{
                  flex: 1,
                  minWidth: '140px',
                  padding: '8px',
                  fontSize: '13px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select a student...</option>
                {students.map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
              <button
                onClick={handleAssign}
                disabled={busy || !assignPlayerId}
                style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  border: 'none',
                  background: '#3498db',
                  color: '#fff',
                  cursor: (busy || !assignPlayerId) ? 'not-allowed' : 'pointer',
                  opacity: (busy || !assignPlayerId) ? 0.5 : 1
                }}
              >
                Assign
              </button>
              <button
                onClick={() => { setAssigning(false); setAssignPlayerId(''); }}
                disabled={busy}
                style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  background: 'white',
                  color: '#7f8c8d',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {showActions && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {isCoach && plan.bookingStatus === 'available' && (
            <button
              onClick={() => onDelete(plan._id)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '4px',
                border: '1px solid #e74c3c',
                background: '#fadbd8',
                color: '#e74c3c',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🗑️ Delete
            </button>
          )}
          {!isCoach && plan.bookingStatus === 'available' && onBook && (
            <button
              onClick={() => onBook(plan._id)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '4px',
                border: 'none',
                background: '#27ae60',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📖 Book Plan
            </button>
          )}
        </div>
      )}
    </div>
  );
}
